/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import ReactRouterPropTypes from "react-router-prop-types";
import { hot } from "react-hot-loader/root";
import { Auth, Hub } from "aws-amplify";
import { withRouter, useLocation } from "react-router-dom";
import moment from "moment";
import jwtDecode from "jwt-decode";
import Nav from "./components/Nav";
import LoginBanner from "./components/LoginBanner";
import SnackBar from "./components/SnackBar";
import MainRouter from "./containers/MainRouter";
import { validateSession } from "./services/authService";
import { ThemeProvider } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import theme from "./theme";
import { MessagesProvider } from "./context/MessagesContext";
import { WorkOrdersProvider } from "./context/WorkOrdersContext";
import { LoadingProvider } from "./context/LoadingContext";
import { getCurrentStoreId, parseQueryStrings } from "./services/helpers/queryParams";
import { RetryPolicyProvider } from "./context/RetryPolicyContext";
import { isOktaUserFeaturesAdmin } from "./services/activeFeatureService";
import useStore from "./hooks/useStore";
import { LicensePlatesProvider } from "./context/LicensePlatesContext";
const { REACT_APP_STORE_DEFAULT, REACT_APP_SC_STAGE} = process.env;


function App(props) {
  const { storeId, setStoreId } = useStore();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { search } = useLocation();
  const [isFeaturesAdmin, setIsFeaturesAdmin] = useState(false);

  useEffect(async () => {
    const queryParams = search && parseQueryStrings(search);
    const currentStoreId = queryParams.length > 0 && getCurrentStoreId(queryParams);
    if (currentStoreId)
      await localStorage.setItem("currentStoreId", currentStoreId);

    //Okta/cognito auth events
    Hub.listen("auth", ({ payload: { event, data } }) => {
      console.log(event);
      switch (event) {
        case "signIn":
          setIsLoggedIn(true);
          break;
        case "signOut":
          setIsLoggedIn(false);
          break;
        case "tokenRefresh":
          break;
        default:
          setIsLoggedIn(false);
      }
    });
    //inStore custom auth events
    Hub.listen("storeAuth", async ({ payload: { event, data } }) => {
      console.log(event);
      setIsFeaturesAdmin(false);
      switch (event) {
        case "signIn":
          const authTime = moment();
          const userInfo = jwtDecode(data);
          const storeNo = userInfo.data.storeId;
          localStorage.setItem("isInStore", true);
          localStorage.setItem("authTime", authTime);
          localStorage.setItem("token", data);
          if (storeNo === "Dev" || parseInt(storeNo) === 1) {
            setIsAdmin(true);
            if (currentStoreId) {
              setStoreId(currentStoreId);
            } else {
              setStoreId(REACT_APP_STORE_DEFAULT);
            }
          } else {
            console.log("settingStore", storeNo);
            localStorage.setItem("defaultStore", parseInt(storeNo));
            setStoreId(parseInt(storeNo));
          }
          if (REACT_APP_SC_STAGE === 'preprod'){
            setIsAdmin(true);
            setStoreId(1);
          }
          setIsLoggedIn(true);
          break;
        case "expired":
          localStorage.clear();
          alert("Your session has expired, press Ok to sign in.");
          setIsLoggedIn(false);
          break;
        case "signOut":
          localStorage.clear();
          setIsLoggedIn(false);
          break;
        default:
          setIsLoggedIn(false);
      }
    });

    initiateAuth();
  }, []);

  useEffect(() => {
    const { history } = props;

    if (isLoggedIn) {
      history.push("/home");
    } else {
      history.push("/");
    }
  }, [isLoggedIn]);

  const initiateAuth = async () => {
    try {
      const isValidated = await validateSession();
      const isInStore = localStorage.getItem("isInStore");
      const queryParams = search && parseQueryStrings(search);
      const currentStoreId =
        queryParams.length > 0 && getCurrentStoreId(queryParams)
          ? getCurrentStoreId(queryParams)
          : localStorage.getItem("currentStoreId");

      if (isValidated && isInStore) {
        const token = localStorage.getItem("token");
        const userInfo = jwtDecode(token);
        if ((userInfo.data.storeId === "Dev" || parseInt(userInfo.data.storeId) === 1)) {
          //only redirect to query param if it's an admin
          setIsAdmin(true);
        } 
        if(currentStoreId) {
          setStoreId(currentStoreId);  
        } else {
          localStorage.setItem("defaultStore", parseInt(userInfo.data.storeId));
          setStoreId(parseInt(userInfo.data.storeId));
        }
        setIsLoggedIn(true);
      } else if (isValidated) {
        const sesh = await (await Auth.currentSession()).getIdToken();
        const { locale, identities } = sesh.payload;
        const storeNo = parseInt(locale);
        if(storeNo === 1) {
          setIsFeaturesAdmin(await isOktaUserFeaturesAdmin(identities[0].userId))
          setIsAdmin(true);
          if (currentStoreId) {
            setStoreId(currentStoreId);
            localStorage.setItem('currentStoreId', currentStoreId);
          }
        } else {
          setIsAdmin(false)
          setStoreId(storeNo);
          localStorage.setItem('currentStoreId', storeNo);
        }
        setIsLoggedIn(true);
      } else {
        setIsLoggedIn(false);
      }
    } catch (error) {
      console.log(`auth error: ${error}`);
      setIsLoggedIn(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <MessagesProvider>
        <LoadingProvider>
          <RetryPolicyProvider>
            <LicensePlatesProvider>
              <WorkOrdersProvider>
                <CssBaseline />
                {isLoggedIn ? (
                  <Nav
                    isAdmin={isAdmin}
                    isLoggedIn={isLoggedIn}
                    storeId={storeId}
                    isFeaturesAdmin={isFeaturesAdmin}
                  />
                ) : (
                  <LoginBanner />
                )}
                <MainRouter
                  {...props}
                  isLoggedIn={isLoggedIn}
                  isAdmin={isAdmin}
                />
                <SnackBar />
              </WorkOrdersProvider>
            </LicensePlatesProvider>
          </RetryPolicyProvider>
        </LoadingProvider>
      </MessagesProvider>
    </ThemeProvider>
  );
}
//note this is the node_env which by default is set to development, not related to the stage.
export default process.env.NODE_ENV === "development"
  ? hot(withRouter(App))
  : withRouter(App);
App.propTypes = {
  history: ReactRouterPropTypes.history,
};
